'use client';

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import LibraryItems from './LibraryItems';
import LibrarySidebar from './LibrarySidebar';
import {
  LibraryCheckbox,
  LearnWatchedItems,
  LoggedInLibraryData,
  LoggedInLibrarySidenavBlockContent,
} from '../../types/library';
import LibraryHeader from './LibraryHeader';
import { track } from '../../lib/util/tracking';
import { usePagesContext } from '../../context/PagesProvider';
import LibraryUserDataProvider from '../../context/LibraryUserDataProvider';
import { formatHash } from '../../lib/shared/library/formatHash';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useAIPanelContext } from '../../context/AIPanelContext';

type Props = {
  libraryData: LoggedInLibraryData;
  checkboxes: LibraryCheckbox[];
  learnWatchedItems: LearnWatchedItems[];
};

const NEW_LABEL = 'new';

const libraryHasNewContent = (
  contents: LoggedInLibrarySidenavBlockContent[],
  l2Label: string,
) => {
  return (
    l2Label == NEW_LABEL || contents.some(({ label }) => label === NEW_LABEL)
  );
};

/**
 * Wrapper for the logged in library.
 */
const LibraryWrapper = (props: Props) => {
  const { libraryData, checkboxes, learnWatchedItems } = props;

  const { params } = usePagesContext();
  const { getClassValue } = useContentSize();
  const { isOpen } = useAIPanelContext();

  const headerProps = libraryData?.header?.blocks[0].args;
  const contents = libraryData?.sidenav?.blocks[0].args?.contents;
  const product = libraryData?.url.split('/')[1];
  const l2HeaderLabel = libraryData?.label || '';

  let showNewContentBanner =
    libraryHasNewContent(contents, l2HeaderLabel) ||
    libraryData?.header?.blocks[0].args?.levelOneTitle.includes('ⓘ') ||
    libraryData?.header?.blocks[0].args?.levelOneTitle === 'PANCE® Review';

  if (product === 'rn') {
    showNewContentBanner = false;
  }

  const topics = libraryData?.videos.blocks;

  const scrollIntoTheView = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return false;

    // 'instant' rather than 'auto': `LibraryScrollWrapper` sets
    // `html { scroll-behavior: smooth }`, which 'auto' defers to, so a
    // load-time deep link animated its way down the page — visible as a flicker
    // when the scroll is later redone against the AI panel's scroll container.
    element.scrollIntoView({
      behavior: 'instant',
      block: 'start',
      inline: 'nearest',
    });
    return true;
  };

  /**
   * Which scroll containers the deep-link scroll has already been performed
   * against, for the currently rendered library data.
   */
  const scrolledContainers = useRef({ document: false, panel: false });

  useLayoutEffect(() => {
    scrolledContainers.current = { document: false, panel: false };
  }, [libraryData]);

  // The AI panel always mounts closed and only restores its persisted open state
  // once the user finishes loading (`isUserLoaded` in AIPanelContext). While it
  // is open, the content column (`#ai-panel-content`) is the scroll container
  // instead of the document, so a scroll performed before the panel opened is
  // silently discarded and the page ends up at the top. Re-assert the scroll
  // once per container so the opening swap is corrected. The panel layout
  // keeps that content scroll container stable after close on desktop.
  // This runs in a layout effect, synchronously after the container swap's DOM
  // mutations, so the browser paints only the corrected position instead of
  // flashing the top of the new container first.
  useLayoutEffect(() => {
    if (window.location.pathname.includes('#')) return;
    if (!params?.libraryUrl) return;

    const container = isOpen ? 'panel' : 'document';
    if (scrolledContainers.current[container]) return;

    const hash = formatHash(params.libraryUrl);
    if (!hash) return;

    scrolledContainers.current[container] = scrollIntoTheView(hash);
  }, [libraryData, isOpen]);

  useEffect(() => {
    const librarySplit = window.location.href.split('/library/');
    const breadcrumbs = librarySplit?.[1]?.replace(/\//g, '|') || '';
    const levels = breadcrumbs.split('|');
    track(
      'library_interaction',
      {
        action: 'visit',
        version: 'reorg',
        breadcrumbs,
        topic: levels[1] || '',
      },
      ['keen'],
    );
  }, []);

  const libraryItemsResponsiveClasses = getClassValue({
    defaultClasses: 'sm:px-8 lg:mt-0',
    breakpoints: {
      sm: 'px-8',
      lg: 'px-8 mt-0',
    },
  });

  const librarySidebarResponsiveClasses = getClassValue({
    defaultClasses: 'w-full md:w-auto',
    breakpoints: {
      md: 'w-auto',
      base: 'w-full',
    },
  });

  return (
    <LibraryUserDataProvider
      checkboxes={checkboxes}
      learnWatchedItems={learnWatchedItems}
    >
      <Box className={`${bg0} min-w-0 overflow-x-hidden`}>
        <Box data-testid="library-header" className={`sticky z-20`}>
          {headerProps && (
            <LibraryHeader
              headerData={headerProps}
              l2HeaderLabel={l2HeaderLabel}
            />
          )}
        </Box>
        <Flex className={`relative flex-row flex-wrap`}>
          <Box
            data-testid="library-sidebar"
            className={cn(
              bg0,
              `border-x-1 border-grey-200 relative h-[auto] border`,
              librarySidebarResponsiveClasses,
            )}
          >
            {contents && (
              <LibrarySidebar
                content={contents}
                hasNewContent={showNewContentBanner}
              />
            )}
          </Box>
          <Box
            className={cn(
              `mx-auto mt-5 max-w-[1280px] flex-1 px-2 pb-16`,
              libraryItemsResponsiveClasses,
            )}
          >
            <Box data-testid="topics-container">
              {topics && <LibraryItems topics={topics} />}
            </Box>
          </Box>
        </Flex>
      </Box>
    </LibraryUserDataProvider>
  );
};

export default React.memo(LibraryWrapper);
